import React, { useState } from "react";
import { Container, Typography, TextField, Paper, Box, IconButton, Dialog, DialogContent, AppBar, Toolbar, Button } from "@mui/material";
import { styled } from "@mui/system";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";

const LogOutput = styled(Paper)(({ theme }) => ({
    whiteSpace: "pre-wrap",
    fontFamily: "monospace",
    fontSize: "0.9rem",
    padding: theme.spacing(2),
    backgroundColor: "#1e1e1e",
    color: "#d4d4d4",
    overflow: "auto",
    maxHeight: "400px",
    position: "relative",
}));

function highlightLog(log) {
    return log
        .replace(/(ERROR)/g, '<span style="color: red; font-weight: bold;">$1</span>')
        .replace(/(ERR)/g, '<span style="color: red; font-weight: bold;">$1</span>')
        .replace(/(WARN|WARNING)/g, '<span style="color: orange;">$1</span>')
        .replace(/(INFO)/g, '<span style="color: lightgreen;">$1</span>')
        .replace(/(DEBUG)/g, '<span style="color: skyblue;">$1</span>')
        .replace(/(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d+Z?)/g, '<span style="color: #999;">$1</span>');
}

export default function LogViewerScreen() {
    const [log, setLog] = useState("");
    const [fullscreen, setFullscreen] = useState(false);
    const [logUrl, setLogUrl] = useState("");

    const errorCount = (log.match(/\b(ERROR|ERR)\b/g) || []).length;

    //   const fetchLogFromUrl = async () => {
    //     if (!logUrl) return;
    //     try {
    //       const proxyUrl = "https://cors-anywhere.herokuapp.com/";
    //       const response = await fetch(`${proxyUrl}${logUrl}`);
    //       const text = await response.text();
    //       setLog(text);
    //     } catch (error) {
    //       setLog(`Failed to fetch log from ${logUrl}\nError: ${error.message}`);
    //     }
    //   };
    const fetchLogFromUrl = async () => {
        if (!logUrl) return;
        try {
            const proxyUrl = "https://cors-anywhere.herokuapp.com/";
            const response = await fetch(`${proxyUrl}${logUrl}`);
            if (response.status === 403) {
                setLog(
                    `⚠️ Access denied (403 Forbidden) — You need to enable this proxy first.\n` +
                    `Visit: https://cors-anywhere.herokuapp.com/corsdemo and click "Request temporary access".\n` +
                    `Then try reloading.`
                );
                return;
            }
            const text = await response.text();
            setLog(text);
        } catch (error) {
            setLog(`Failed to fetch log from ${logUrl}\nError: ${error.message}`);
        }
    };

    return (
        <Container maxWidth="md" sx={{ mt: 4 }}>
            <Typography variant="h4" gutterBottom>
                Log Viewer
            </Typography>
            <Typography variant="subtitle1" color="error" gutterBottom>
                Errors Found: {errorCount}
            </Typography>

            <Box display="flex" gap={2} mb={2}>
                <TextField
                    label="Log URL (http/https)"
                    fullWidth
                    variant="outlined"
                    value={logUrl}
                    onChange={(e) => setLogUrl(e.target.value)}
                />
                <Button variant="contained" onClick={fetchLogFromUrl} startIcon={<RefreshIcon />}>
                    Load / Reload
                </Button>
            </Box>

            <TextField
                label="Paste your log here"
                multiline
                fullWidth
                rows={10}
                variant="outlined"
                value={log}
                onChange={(e) => setLog(e.target.value)}
                sx={{ mb: 3 }}
            />

            <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="h6" gutterBottom>
                    Formatted Log:
                </Typography>
                <IconButton color="inherit" onClick={() => setFullscreen(true)}>
                    <FullscreenIcon />
                </IconButton>
            </Box>

            <LogOutput
                elevation={3}
                dangerouslySetInnerHTML={{ __html: highlightLog(log) }}
            />

            <Dialog fullScreen open={fullscreen} onClose={() => setFullscreen(false)}>
                <AppBar sx={{ position: 'relative', backgroundColor: '#1e1e1e' }}>
                    <Toolbar>
                        <Typography sx={{ flex: 1 }} variant="h6" component="div">
                            Fullscreen Log Output
                        </Typography>
                        <IconButton edge="end" color="inherit" onClick={() => setFullscreen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Toolbar>
                </AppBar>
                <DialogContent>
                    <Box sx={{ p: 2 }}>
                        <div
                            style={{
                                whiteSpace: "pre-wrap",
                                fontFamily: "monospace",
                                fontSize: "0.9rem",
                                backgroundColor: "#1e1e1e",
                                color: "#d4d4d4",
                                padding: "16px",
                                overflow: "auto",
                            }}
                            dangerouslySetInnerHTML={{ __html: highlightLog(log) }}
                        />
                    </Box>
                </DialogContent>
            </Dialog>
        </Container>
    );
}
